import { useCallback, useMemo } from "react";

import {
  type Referral,
  type ReferralFilters,
  type ReferralQueryParams,
  ReferralQueryParamsSchema,
  referralsEndpoints,
} from "../../../api";
import { useStrapiList, type UseStrapiListOptions } from "../../../hooks/strapi";
import getErrorMessage from "../../../utils/getErrorMessage";
import handleError from "../../utils/handleError";

interface UseReferralsProps extends Omit<UseStrapiListOptions, "filters"> {
  mine?: boolean;
  fields?: string[];
  filters?: ReferralFilters;
}

const useReferrals = ({ mine, fields, filters, ...otherProps }: UseReferralsProps = {}) => {
  // Stable load function - only changes when mine or fields change
  const isMine = !!mine;
  const serializedFields = useMemo(() => (fields !== undefined ? JSON.stringify(fields) : undefined), [fields]);
  const load = useCallback(
    (params: Record<string, unknown>) => {
      const merged = {
        ...params,
        ...(serializedFields !== undefined ? { fields: JSON.parse(serializedFields) } : {}),
      };
      const parsed = ReferralQueryParamsSchema.parse(merged);
      return loadReferrals(parsed, isMine);
    },
    [isMine, serializedFields]
  );

  // Stable error handler
  const onLoadError = useCallback((err: unknown) => {
    handleError(getErrorMessage(err, "Referrals loading failed"));
  }, []);

  const {
    items: referrals,
    refresh,
    isLoading,
    ...other
  } = useStrapiList<Referral>({
    ...otherProps,
    filters,
    load,
    onLoadError,
  });

  return {
    referrals,
    refresh,
    isLoading,
    ...other,
  };
};

export const loadReferrals = async (params: ReferralQueryParams, mine?: boolean) => {
  try {
    return mine ? referralsEndpoints.getMyReferrals(params) : referralsEndpoints.getReferrals(params);
  } catch (err: unknown) {
    handleError(getErrorMessage(err, "Referrals loading failed"));
    return undefined;
  }
};

export default useReferrals;
